<template>
  <ResourceDialog
    ref="dialog"
    fullscreen
  >
    <template #form>
      <div v-if="search">
        <div v-if="mode == 'location'">
          <template v-if="searchFlags.regionSearchEnabled">
            <div class="fs-20 mb-4">
              {{ $bct('Search by zip code, city or county') }}
            </div>
            <v-text-field
              v-model="search.unformatted_origin"
              @keyup.enter="submitDisabled ? false : submit()"
              :placeholder="$bct('Enter zip code, city or county')"
              variant="outlined"
              hide-details
            />
          </template>
          <template v-else>
            <LocationEditor
              :search="search"
              hide-close
            />
          </template>
        </div>
        <div v-if="mode == 'mode'">
          <div class="c-black fs-18 fw-600 mb-2">
            {{ $bct('Show commutes by') + ': ' }}
          </div>
          <v-list
            v-model:selected="search.travel_mode"
            mandatory
          >
            <v-list-item
              v-for="type in $a.assets.transit_options"
              :key="type.value"
              :value="type.value"
            >
              <v-list-item-title>{{ $bct(type.text) }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </div>
        <div v-if="mode == 'children' && !searchFlags.dobSearchEnabled">
          <div class="c-black fs-18 fw-600 mb-2">
            {{ $bct('Add children') + ': ' }}
          </div>
          <LabeledVolume
            v-for="(item, index) in ageGroups"
            @decrease="$emit('change:count', [index, -1])"
            @increase="$emit('change:count', [index, 1])"
            :key="index"
            ref="smChildVolume"
            :count="item.count"
            :dense="true"
            :subtitle="item.subtitle || item.subtext"
            :title="item.title || item.text"
          />
        </div>

        <div v-if="mode == 'children' && searchFlags.dobSearchEnabled">
          <SearchDobChild
            @on-add-child="(child) => $emit('onAddChild', child)"
            @on-remove-child="(index) => $emit('onRemoveChild', index)"
            @on-update-child="(child, index) => $emit('onUpdateChild', child, index)"
            ref="searchDobChild"
            :children="children"
            :is-mobile="true"
          />
        </div>
      </div>
    </template>

    <template #actions>
      <v-btn
        @click="close"
        :disabled="searchDialogDoneDisabled"
        :ripple="false"
        class="focus-very-visible"
        color="primary"
        data-cy="done_button"
        block
      >
        {{ $bct('Done') }}
      </v-btn>
    </template>
  </ResourceDialog>
</template>

<script setup>
import LabeledVolume from '@/shared/components/form/LabeledVolume.vue';
import LocationEditor from '@/shared/components/search/LocationEditor.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import SearchDobChild from '@/parent/components/searches/SearchDobChild.vue';
import SearchFlags from '@/parent/services/search-flags';
import { useStore } from 'vuex';

const props = defineProps({
  ageGroups: {
    type: Array,
    default: () => [],
  },
  children: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'change',
  'change:count',
  'onAddChild',
  'onRemoveChild',
  'onUpdateChild',
]);

const store = useStore();
const $bct = inject('translator');

const dialog = ref(null);
const searchDobChild = ref(null);
const mode = ref(null);
const search = ref(null);
const searchFlags = new SearchFlags(store);

const childrenDoneDisabled = computed(() => {
  if (searchFlags.regionSearchEnabled) return false;

  let count = 0;

  if (searchFlags.dobSearchEnabled) {
    count = props.children?.filter(
      (child) => child.dob !== null && child.care_needed_date !== null,
    ).length;
  } else {
    count = props.ageGroups?.filter((ageGroup) => ageGroup.count > 0).length;
  }

  return count === 0;
});

const searchDialogDoneDisabled = computed(() => {
  if (
    mode.value === 'location' &&
    !search.value.unformatted_origin &&
    !search.value.formatted_origin
  ) {
    return true;
  }
  if (mode.value === 'mode' && !search.value.travel_mode) return true;
  if (mode.value === 'children' && childrenDoneDisabled.value) return true;

  return false;
});

const handleClose = () => {
  emit('change', search.value);
  dialog.value.close();
  mode.value = null;
  search.value = null;
};

const close = () => {
  if (searchFlags.dobSearchEnabled && mode.value === 'children') {
    // We need to handle the children menu differently based on the UX specific to this feature
    // only when dob search is enabled

    // Checks for validity (func returns false if invalid)
    if (searchDobChild.value?.handleDoneClick() === true) {
      handleClose();
    }
  } else {
    handleClose();
  }
};

const open = (newMode, newSearch) => {
  mode.value = newMode;
  search.value = newSearch;
  dialog.value.open();
};

defineExpose({
  open,
});
</script>
